import {
  getChartTitle,
  padBlockForExport,
  replaceBlockMarkers,
  tableDataToMarkdown,
} from '@spotify/confidence-conversation-react';
import { PulseV1PulseBlockType } from '@spotify/confidence-graphql';

import { stripPulseNumberMarkers } from '../MarkdownContent/inlineBlockMarkers';
import { type InlineBlocksIndex, resolveInlineBlock } from './inlineBlocks';

export type FormatMessageForExportArgs = {
  content: string;
  inlineBlocks: InlineBlocksIndex | undefined;
  eventName?: string;
};

export const formatMessageForExport = ({
  content,
  inlineBlocks,
  eventName,
}: FormatMessageForExportArgs): string => {
  const expand = (source: string, expansionPath: ReadonlySet<string>): string =>
    replaceBlockMarkers(
      source,
      ({ name, standalone, blankLineBefore, blankLineAfter }) => {
        if (!inlineBlocks) return '';
        if (expansionPath.has(name)) return '';
        const block = resolveInlineBlock(inlineBlocks, name, eventName);
        if (!block) return '';

        const padBlock = (text: string): string =>
          padBlockForExport(text, { blankLineBefore, blankLineAfter });

        if (block.type === PulseV1PulseBlockType.PulseBlockTypeTable) {
          const label = `Table: ${name}`;
          return standalone
            ? padBlock(tableDataToMarkdown(block.table))
            : label;
        }

        if (block.type === PulseV1PulseBlockType.PulseBlockTypeText) {
          const expandedContent = expand(
            block.content,
            new Set([...expansionPath, name]),
          );
          return standalone ? padBlock(expandedContent) : expandedContent;
        }

        const title = getChartTitle(block.spec) ?? name;
        const chartLabel = `Chart: ${title}`;
        return standalone ? padBlock(`> ${chartLabel}`) : chartLabel;
      },
    );

  const withBlocks = expand(content, new Set());

  return stripPulseNumberMarkers(withBlocks);
};
